/**
 * Control Portal - Global Audit Logs
 * System-wide audit log viewer
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { controlApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  FileText, RefreshCw, Loader2, Search, Filter, Calendar,
  User, Globe, Activity, Clock, ChevronLeft, ChevronRight
} from 'lucide-react';

export default function GlobalAuditPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({});
  const [entityTypes, setEntityTypes] = useState([]);
  const [actions, setActions] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    action: 'all',
    entity_type: 'all',
    ip_address: '',
    from: '',
    to: ''
  });
  const [page, setPage] = useState(0);
  const pageSize = 50;

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        limit: pageSize,
        skip: page * pageSize,
        ...(filters.action && filters.action !== 'all' && { action: filters.action }),
        ...(filters.entity_type && filters.entity_type !== 'all' && { entity_type: filters.entity_type }),
        ...(filters.ip_address && { ip_address: filters.ip_address }),
        ...(filters.from && { from: filters.from }),
        ...(filters.to && { to: filters.to })
      };
      
      const [logsRes, statsRes, entityTypesRes, actionsRes] = await Promise.all([
        controlApi.getGlobalAuditLogs(params),
        controlApi.getGlobalAuditStats({}),
        controlApi.getAuditEntityTypes(),
        controlApi.getAuditActions()
      ]);
      
      setLogs(logsRes.data);
      setStats(statsRes.data);
      setEntityTypes(entityTypesRes.data);
      setActions(actionsRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, filters.action, filters.entity_type]);

  const handleSearch = () => {
    setPage(0);
    fetchData();
  };

  const clearFilters = () => {
    setFilters({
      action: 'all',
      entity_type: 'all',
      ip_address: '',
      from: '',
      to: ''
    });
    setPage(0);
  };

  const getActionColor = (action) => {
    if (action?.includes('create') || action?.includes('register')) return 'bg-green-500/20 text-green-400 border-green-500/30';
    if (action?.includes('delete') || action?.includes('revoke') || action?.includes('suspend')) return 'bg-red-500/20 text-red-400 border-red-500/30';
    if (action?.includes('update') || action?.includes('extend')) return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    if (action?.includes('auth') || action?.includes('login')) return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
  };

  return (
    <div className="p-6" data-testid="global-audit-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <FileText className="w-7 h-7 text-blue-500" />
            System Audit Logs
          </h1>
          <p className="text-slate-400 mt-1">Global activity monitoring across all tenants</p>
        </div>
        <Button variant="outline" onClick={fetchData} className="border-slate-700 text-slate-300">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-500/20">
              <Activity className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.total_logs?.toLocaleString() || 0}</p>
              <p className="text-slate-400 text-sm">Total Events</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-green-500/20">
              <Clock className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.today || 0}</p>
              <p className="text-slate-400 text-sm">Today</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-teal-500/20">
              <Calendar className="w-5 h-5 text-teal-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.this_week || 0}</p>
              <p className="text-slate-400 text-sm">This Week</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-amber-500/20">
              <Globe className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{entityTypes.length}</p>
              <p className="text-slate-400 text-sm">Entity Types</p>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Breakdown */}
      {stats.by_action && stats.by_action.length > 0 && (
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-4 mb-6">
          <h3 className="text-white font-semibold mb-3">Top Actions</h3>
          <div className="flex flex-wrap gap-2">
            {stats.by_action.slice(0, 8).map((item) => (
              <div key={item.action} className="bg-slate-800 rounded-lg px-3 py-1.5 flex items-center gap-2">
                <span className="text-slate-300 text-sm">{item.action}</span>
                <Badge variant="secondary" className="text-xs">{item.count}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-slate-400" />
          <span className="text-slate-300 font-medium">Filters</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Select value={filters.action} onValueChange={(v) => setFilters({ ...filters, action: v })}>
            <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
              <SelectValue placeholder="Action" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700 max-h-[300px]">
              <SelectItem value="all" className="text-white">All Actions</SelectItem>
              {actions.map((action) => (
                <SelectItem key={action} value={action} className="text-white">{action}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.entity_type} onValueChange={(v) => setFilters({ ...filters, entity_type: v })}>
            <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
              <SelectValue placeholder="Entity Type" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all" className="text-white">All Entities</SelectItem>
              {entityTypes.map((type) => (
                <SelectItem key={type} value={type} className="text-white">{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            placeholder="IP Address"
            value={filters.ip_address}
            onChange={(e) => setFilters({ ...filters, ip_address: e.target.value })}
            className="bg-slate-800 border-slate-700 text-white"
          />
          <Input
            type="datetime-local"
            value={filters.from}
            onChange={(e) => setFilters({ ...filters, from: e.target.value })}
            className="bg-slate-800 border-slate-700 text-white"
            placeholder="From"
          />
          <div className="flex gap-2">
            <Button onClick={handleSearch} className="bg-blue-600 hover:bg-blue-700 flex-1">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
            <Button variant="outline" onClick={clearFilters} className="border-slate-700 text-slate-300">
              Clear
            </Button>
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-800 hover:bg-slate-800/50">
              <TableHead className="text-slate-400">Timestamp</TableHead>
              <TableHead className="text-slate-400">Tenant</TableHead>
              <TableHead className="text-slate-400">Action</TableHead>
              <TableHead className="text-slate-400">Entity</TableHead>
              <TableHead className="text-slate-400">IP Address</TableHead>
              <TableHead className="text-slate-400">Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto" />
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No audit logs found</p>
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log.id} className="border-slate-800 hover:bg-slate-800/50">
                  <TableCell className="text-slate-300 text-sm whitespace-nowrap">
                    {new Date(log.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {log.tenant_name ? (
                      <Badge variant="outline" className="border-slate-600">{log.tenant_name}</Badge>
                    ) : (
                      <span className="text-slate-500">System</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={getActionColor(log.action)}>
                      {log.action}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-slate-300">{log.entity_type || '-'}</span>
                    {log.entity_id && (
                      <span className="text-slate-500 text-xs ml-1">
                        ({log.entity_id.substring(0, 8)}...)
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-slate-400 font-mono text-sm">
                    {log.ip_address || '-'}
                  </TableCell>
                  <TableCell className="text-slate-400 text-sm max-w-[200px] truncate">
                    {log.extra_data ? JSON.stringify(log.extra_data).substring(0, 50) : '-'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800">
          <p className="text-slate-400 text-sm">
            Showing {page * pageSize + 1} - {page * pageSize + logs.length}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="border-slate-700 text-slate-300"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={logs.length < pageSize}
              className="border-slate-700 text-slate-300"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
